import React from 'react'

import { ImportRequestStatus as ImportRequestStatusBadge } from 'modules/requests/components/ImportRequestStatus'
import * as metaStyles from 'modules/resultsV2/ResultMeta.styles'
import { getImportRequestScanResultMessage } from 'modules/resultsV2/hooks/useImportRequestScanDetails'
import {
  getImportRequestScanDetailsProps,
  isExternalImportRequestDetail
} from 'services/import-requests/importRequestUtils'
import type { ImportRequestVerificationDetail } from 'services/import-requests/types'
import { formatDate } from 'utils/date'

export const IMPORT_REQUEST_METADATA_TEST_ID = 'import-request-metadata'

export const UNASSIGNED_LABEL = 'Unassigned'

const EMPTY_VALUE = '-'

export interface ImportRequestDetailsMetadataProps {
  importRequest: ImportRequestVerificationDetail
}

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <metaStyles.MetadataRow>
    <metaStyles.MetadataLabel>{label}</metaStyles.MetadataLabel>
    <metaStyles.MetadataValue>{children}</metaStyles.MetadataValue>
  </metaStyles.MetadataRow>
)

/**
 * The facts about an import request, below the file and its progress. Mirrors the metadata list
 * the export and data access pages show, so the three request types read the same way.
 */
export const ImportRequestDetailsMetadata = ({ importRequest }: ImportRequestDetailsMetadataProps): JSX.Element => {
  // External requests derive their scan result rather than carrying the field, so read it the
  // same way the scan card does instead of off the request.
  const { scanResult, counts } = getImportRequestScanDetailsProps(importRequest)

  return (
    <metaStyles.MetadataSection data-testid={IMPORT_REQUEST_METADATA_TEST_ID}>
      <Row label="Status">
        <ImportRequestStatusBadge status={importRequest.status} />
      </Row>
      <Row label="Requested by">{importRequest.requestingUser?.email ?? EMPTY_VALUE}</Row>
      <Row label="Requested on">{formatDate(importRequest.createdAt)}</Row>
      <Row label="Request type">{isExternalImportRequestDetail(importRequest) ? 'External' : 'Upload'}</Row>
      {scanResult !== undefined && (
        <Row label="Scan result">{getImportRequestScanResultMessage(scanResult, counts)}</Row>
      )}
      <Row label="Workspace">{importRequest.workspaceName ?? EMPTY_VALUE}</Row>
      <Row label="Workspace ID">{importRequest.workspaceId}</Row>
      <Row label="Assignee">{importRequest.assignedTo?.email ?? UNASSIGNED_LABEL}</Row>
    </metaStyles.MetadataSection>
  )
}
